import { useState, useMemo, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import type { RadarBubble } from '@/services/api/teacher'
import { Modal } from '@/components/common/Modal'
import { GlassButton } from './GlassButton'

interface CancelBubbleModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedLessonRequestIds: string[]) => void
  currentBubble?: RadarBubble | null
  siblingBubbles: RadarBubble[]
  cancelling: boolean
}

interface BubbleEntry {
  bubble: RadarBubble
  role: string
  label: string
  color: string
  bgColor: string
  lessonIds: string[]
  lessonCount: number
  isDeclined: boolean
  subjectInfo: string
  dateInfo: string
}

const SPLIT_ROLE_META: Record<string, { label: string; color: string; bgColor: string }> = {
  filled: { label: 'Übernommen', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200' },
  declined: { label: 'Abgelehnt', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200' },
  pending: { label: 'Offen', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200' },
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleDateString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    })
  } catch {
    return ''
  }
}

export function CancelBubbleModal({
  isOpen,
  onClose,
  onConfirm,
  currentBubble,
  siblingBubbles,
  cancelling,
}: CancelBubbleModalProps) {
  const allEntries = useMemo<BubbleEntry[]>(() => {
    const bubbles = [currentBubble, ...siblingBubbles].filter(
      (b): b is RadarBubble => b != null,
    )
    return bubbles
      .filter(b => b.is_split || b.lesson_request_ids?.length)
      .map(b => {
        const role = b.split_role || 'pending'
        const meta = SPLIT_ROLE_META[role] || SPLIT_ROLE_META['pending']!
        const lessonIds = b.lesson_request_ids || []
        const subjectParts = [b.subject_name, b.klass_name, b.room_name].filter(Boolean)
        return {
          bubble: b,
          role,
          label: meta.label,
          color: meta.color,
          bgColor: meta.bgColor,
          lessonIds,
          lessonCount: b.lesson_count || lessonIds.length || 1,
          isDeclined: role === 'declined',
          subjectInfo: subjectParts.join(' | ') || '—',
          dateInfo: formatDate(b.lesson_date),
        }
      })
  }, [currentBubble, siblingBubbles])

  const allSelectableIds = useMemo(() => {
    const ids: string[] = []
    for (const entry of allEntries) {
      if (!entry.isDeclined) {
        for (const id of entry.lessonIds) {
          ids.push(id)
        }
      }
    }
    return ids
  }, [allEntries])

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen && allEntries.length > 0) {
      const initial = new Set<string>()
      for (const entry of allEntries) {
        if (!entry.isDeclined) {
          for (const id of entry.lessonIds) {
            initial.add(id)
          }
        }
      }
      setCheckedIds(initial)
    }
  }, [isOpen, allEntries])

  const toggleBubble = (entry: BubbleEntry) => {
    if (cancelling) return
    setCheckedIds(prev => {
      const next = new Set(prev)
      const allChecked = entry.lessonIds.every(id => next.has(id))
      if (allChecked) {
        entry.lessonIds.forEach(id => next.delete(id))
      } else {
        entry.lessonIds.forEach(id => next.add(id))
      }
      return next
    })
  }

  const selectAll = () => {
    setCheckedIds(new Set(allSelectableIds))
  }

  const deselectAll = () => {
    setCheckedIds(new Set())
  }

  const handleConfirm = () => {
    onConfirm(Array.from(checkedIds))
  }

  const totalLessons = allEntries.reduce((sum, e) => sum + e.lessonIds.length, 0)
  const selectedCount = checkedIds.size
  const everythingSelected = allSelectableIds.length > 0 && allSelectableIds.every(id => checkedIds.has(id))
  const nothingSelected = checkedIds.size === 0
  const selectAllDisabled = allSelectableIds.length === 0 || everythingSelected

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Lektionen stornieren"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Wählen Sie die Lektionen aus, die storniert werden sollen.
        </p>

        <div className="space-y-2">
          {allEntries.map((entry) => {
            const allChecked = entry.lessonIds.every(id => checkedIds.has(id))
            const someChecked = entry.lessonIds.some(id => checkedIds.has(id))

            return (
              <div
                key={entry.bubble.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition ${cancelling ? 'opacity-60' : ''} ${entry.bgColor}`}
              >
                <input
                  type="checkbox"
                  checked={allChecked}
                  disabled={cancelling}
                  ref={el => {
                    if (el) el.indeterminate = someChecked && !allChecked
                  }}
                  onChange={() => toggleBubble(entry)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${entry.color}`}>
                      {entry.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {entry.lessonIds.length} {entry.lessonIds.length === 1 ? 'Lektion' : 'Lektionen'}
                    </span>
                    {entry.isDeclined && (
                      <span className="text-xs text-red-500 italic">
                        (Bereits abgelehnt)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span className="truncate">{entry.subjectInfo}</span>
                    {entry.dateInfo && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="whitespace-nowrap">{entry.dateInfo}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <span className="text-sm text-gray-600">
            {selectedCount} von {totalLessons} Lektionen ausgewählt
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAll}
              disabled={cancelling || selectAllDisabled}
              className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Alle auswählen
            </button>
            <button
              onClick={deselectAll}
              disabled={cancelling || nothingSelected}
              className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Alle abwählen
            </button>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <GlassButton
            onClick={handleConfirm}
            disabled={selectedCount === 0 || cancelling}
            variant="danger"
            size="sm"
            className="flex-1"
          >
            {cancelling ? (
              <>Storniere...</>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                {selectedCount} {selectedCount === 1 ? 'Lektion stornieren' : 'Lektionen stornieren'}
              </>
            )}
          </GlassButton>
          <GlassButton
            onClick={onClose}
            disabled={cancelling}
            variant="secondary"
            size="sm"
          >
            Abbrechen
          </GlassButton>
        </div>
      </div>
    </Modal>
  )
}
